<template>
  <div class="cart-item relative">
    <div class="flex pl-3 md:pl-6 py-6">
      <a
        class="flex-shrink-0 w-12 h-12 md:w-24 md:h-24"
        :href="item.canonical"
        v-lazyload
      >
        <img
          class="object-cover"
          src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23F5F5F5'/%3E%3C/svg%3E"
          :data-src="item.swatch"
          :alt="item.name"
        />
      </a>

      <div class="flex flex-col flex-grow leading-snug pl-4">
        <a
          class="text-gray-800 leading-tight pr-8 md:pr-0 no-underline hover:underline"
          :href="item.canonical"
        >
          <b>{{ item.product_name }}</b>
          {{ item.variant_name }}
        </a>

        <div class="text-gray-800 text-sm">
          {{ item.sku }}
        </div>

        <div class="text-base whitespace-nowrap">
          <template v-if="item.product_type === 'Collated Set'">
            <span class="text-gray-600 text-sm">Memo Samples</span>
          </template>
          <template v-else-if="item.product_type === 'Sample Card'">
            <span class="text-gray-600 text-sm">Sample Card</span>
          </template>
          <template v-else-if="item.product_type === 'Product' && item.warehouse === '03'">
            <span class="text-gray-600 text-sm">Memo Sample</span>
          </template>
          <template v-else>
            <span class="text-gray-800">
              {{ formattedPrice(item.price) }}
            </span>
            <span class="text-gray-600 text-sm lowercase"> /{{ item.uom }} </span>
          </template>
        </div>

        <div
          v-if="item.discontinued"
          class="text-gray-800 text-sm italic leading-tight mt-2"
        >
          Discontinued
        </div>

        <div
          class="text-gray-600 text-sm leading-tight mt-4"
          v-if="item.note"
        >
          {{ item.note }}
        </div>

        <div class="relative mt-4 md:mt-auto">
          <button
            type="button"
            class="text-xs font-semibold text-left"
            @click="isNoteModalOpen = true"
          >
            <span v-if="!item.note">
              <span>+</span>
              <span>Add Note</span>
            </span>
            <span v-else>
              <span>-</span>
              <span>Edit or Remove&nbsp;Note</span>
            </span>
          </button>
        </div>
      </div>

      <div class="lg:w-56 px-4 pb-2 flex flex-col flex-shrink-0 justify-start">
        <div class="flex flex-wrap lg:flex-nowrap lg:items-center justify-end w-20 lg:w-auto">
          <label class="flex items-center">
            <span class="sr-only lg:not-sr-only lg:block w-14 text-right text-base uppercase"
              >Qty:</span
            >
            <input
              type="number"
              class="border-gray-300 w-20 text-xs text-gray-800 font-semibold lg:ml-4"
              min="1"
              :value="parseFloat(item.quantity)"
              :disabled="isSaving"
              ref="quantity-input"
              @focus="$event.target.select()"
              @blur="updateItem(updateQuantity)"
              @input="$emit('dirty')"
            />
          </label>
          <button
            type="button"
            class="flex items-center justify-end w-14 h-8 hover:text-black ml-2 uppercase text-xs text-right hover:underline"
            title="Remove"
            :disabled="isSaving"
            @click="deleteItem"
          >
            Remove
          </button>
        </div>

        <div
          v-if="item.product_type === 'Product' && item.warehouse === '01'"
          class="hidden lg:flex justify-end text-base mt-8 pr-10"
        >
          <span class="w-14 text-right uppercase">Total:</span>
          <span class="w-20 font-bold text-right ml-4">{{ lineTotal }}</span>
        </div>
      </div>
    </div>

    <FocusTrap
      v-model="isNoteModalOpen"
      :initial-focus="() => $refs['note-input']"
    >
      <div
        tabindex="-1"
        class="fixed inset-0 flex items-center justify-center z-10 fade-in animate-fast"
        style="background-color: rgba(0, 0, 0, 0.3)"
        v-show="isNoteModalOpen"
        @click.self="isNoteModalOpen = false"
      >
        <div class="w-full max-w-md flex flex-col bg-gray-100 p-4">
          <label class="flex flex-col">
            <span class="text-sm">Optional Note for {{ item.name }}</span>
            <input
              type="text"
              class="border-gray-300 w-full text-sm mt-4"
              placeholder="Note (optional)"
              :value="item.note"
              :disabled="isSaving"
              ref="note-input"
            />
          </label>

          <div class="flex justify-end mt-4">
            <button
              type="button"
              class="button text-gray-800 hover:text-black w-24 px-0 py-2"
              @click="isNoteModalOpen = false"
              :disabled="isSaving"
            >
              Cancel
            </button>

            <button
              type="button"
              class="button button--primary w-24 px-0 py-2 ml-2"
              :disabled="isSaving"
              @click="updateItem(updateNote)"
            >
              <span :class="{ hidden: !isSaving }">
                <SvgLoader
                  class="inline-svg"
                  size="1rem"
                ></SvgLoader>
              </span>
              <span :class="{ hidden: isSaving }">Save</span>
            </button>
          </div>
        </div>
      </div>
    </FocusTrap>

    <div
      class="absolute inset-0 flex items-center justify-center bg-gray-300 opacity-50"
      :class="{ hidden: isNoteModalOpen || !isSaving }"
    >
      <SvgLoader
        class="text-gray-800"
        size="4rem"
      ></SvgLoader>
    </div>
  </div>
</template>

<script>
  import { FocusTrap } from "focus-trap-vue";
  import SvgLoader from "@components/Common/SvgLoader";
  import Modal from "@/utility/modal_triggers";
  import { moneyFormat } from "@/utility/functions";

  export default {
    name: "CartItem",

    components: {
      FocusTrap,
      SvgLoader,
    },

    props: {
      item: {
        default() {
          return {
            discontinued: false,
            instance: null,
            name: null,
            note: null,
            price: 0.0,
            product_name: null,
            product_type: null,
            quantity: 0,
            sku: null,
            swatch: null,
            variant_name: null,
            uom: "EA",
            warehouse: null,
          };
        },
        type: Object,
      },
    },

    data() {
      return {
        isNoteModalOpen: false,
        isSaving: false,
      };
    },

    computed: {
      lineTotal() {
        return this.formattedPrice(this.item.price * this.item.quantity);
      },
    },

    methods: {
      deleteItem() {
        if (!confirm("Are you sure you want to remove this item from your cart?")) {
          return;
        }

        this.isSaving = true;

        let data = new FormData();
        data.append("_method", "delete");
        data.append("sku", this.item.sku);
        data.append("warehouse", this.item.warehouse);

        this.$http
          .post("/cart/remove", data)
          .finally(() => (this.isSaving = false))
          .then((response) => {
            this.$emit("remove-item", this.item);
          })
          .catch((error) => window.console.error(error.response));
      },

      formattedPrice(price) {
        return moneyFormat(price);
      },

      updateItem(callback) {
        this.isSaving = true;

        let data = new FormData();
        data.append("_method", "patch");
        data.append("sku", this.item.sku);
        data.append("warehouse", this.item.warehouse);
        data.append("quantity", Math.max(1, this.$refs["quantity-input"].value));
        data.append("note", this.$refs["note-input"].value);

        this.$http
          .post("/cart/update", data)
          .finally(() => {
            this.isSaving = false;
            this.$emit("clean");
          })
          .then((response) => {
            this.$emit("update-cart");

            if (typeof callback === "function") {
              callback.call(this, response.data);
            }
          })
          .catch((error) => {
            error.response?.data?.html && new Modal(error.response.data.html);
          });
      },

      updateQuantity(data) {
        this.$set(this.item, "quantity", data.item.quantity);
      },

      updateNote(data) {
        this.$set(this.item, "note", data.item.note);

        this.isNoteModalOpen = false;
      },
    },

    watch: {
      isSaving(state) {
        this.$emit("processing", this.item.sku, state);
      },

      isNoteModalOpen(state) {
        window.document.body.classList.toggle("overflow-hidden", state);
      },
    },
  };
</script>
